<template>
  <div id="adminSlider">
    <app-menu></app-menu>
    <div class="col-lg-10 offset-lg-1 pt-4">

      <h3 class="text-center pb-3">Slider – {{ config ? config.label : '' }}</h3>

      <div v-if="!config" class="alert alert-danger text-center">
        Neznámý slider.
      </div>

      <template v-if="config">
        <!-- Jazykový přepínač (pouze pro slidery s více jazyky) -->
        <div v-if="config.hasLanguages" class="text-center mb-4">
          <button
            class="btn btn-sm mr-2"
            :class="lang === 'cz' ? 'btn-dark' : 'btn-outline-secondary'"
            @click="switchLang('cz')"
          >
            🇨🇿 Čeština
          </button>
          <button
            class="btn btn-sm"
            :class="lang === 'en' ? 'btn-dark' : 'btn-outline-secondary'"
            @click="switchLang('en')"
          >
            🇬🇧 English
          </button>
        </div>

        <!-- Upload nových obrázků -->
        <div class="form-group">
          <label><strong>Přidat obrázky (JPG):</strong></label>
          <input
            ref="fileInput"
            type="file"
            class="form-control-file pt-1"
            accept="image/jpeg"
            multiple
            @change="selectImages"
          />
          <small class="form-text text-muted">
            Obrázky se přidají na konec seznamu. Pořadí měňte šipkami ▲ ▼.
          </small>
        </div>

        <!-- Seznam obrázků -->
        <div v-if="currentImages.length > 0" class="pt-2">
          <div
            v-for="(img, idx) in currentImages"
            :key="img.key"
            class="imageRow d-flex align-items-center mb-3 p-2"
          >
            <!-- Pořadí + šipky -->
            <div class="orderControls mr-3 text-center flex-shrink-0">
              <div class="orderNum">{{ idx + 1 }}.</div>
              <button
                class="btn btn-sm btn-outline-secondary d-block w-100 mt-1"
                :disabled="idx === 0"
                title="Posunout nahoru"
                @click="moveUp(idx)"
              >▲</button>
              <button
                class="btn btn-sm btn-outline-secondary d-block w-100 mt-1"
                :disabled="idx === currentImages.length - 1"
                title="Posunout dolů"
                @click="moveDown(idx)"
              >▼</button>
            </div>

            <!-- Náhled obrázku -->
            <div class="imagePreviewWrap flex-grow-1 position-relative">
              <span v-if="img.isNew" class="newBadge">Nový</span>
              <img class="sliderPreview" :src="img.src" alt="Banner" />
            </div>

            <!-- Smazat -->
            <div class="ml-3 flex-shrink-0">
              <button class="btn btn-danger btn-sm" @click="removeImage(idx)">
                ✕ Smazat
              </button>
            </div>
          </div>
        </div>

        <div v-else class="alert alert-info text-center mt-3">
          Zatím žádné obrázky pro tento slider.
          Přidejte obrázky výše a klikněte na <strong>Uložit</strong>.
        </div>

        <!-- Uložit -->
        <div class="text-center pt-4 pb-4">
          <button
            class="btn btn-success btn-lg"
            :disabled="saving"
            @click="save"
          >
            <span v-if="saving">
              <span class="spinner-border spinner-border-sm mr-2" role="status"></span>
              Ukládám…
            </span>
            <span v-else>Uložit</span>
          </button>
          <div v-if="successMessage" class="text-success mt-2 font-weight-bold">
            {{ successMessage }}
          </div>
          <div v-if="error" class="text-danger mt-2">
            Chyba při ukládání: {{ error.message || error }}
          </div>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import AdminMenu from "@/components/admin/AdminMenu";
import axios from "@/api/axiom-instance";

const SLIDER_CONFIGS = {
  home: {
    label: "Hlavní strana",
    hasLanguages: true,
    typeCz: "slider-home-cz",
    typeEn: "slider-home-en",
  },
  busking: {
    label: "Busking",
    hasLanguages: false,
    type: "slider-busking",
  },
  about: {
    label: "O festivalu",
    hasLanguages: false,
    type: "slider-about",
  },
};

let keyCounter = 0;

export default {
  name: "AdminSlider",
  components: { "app-menu": AdminMenu },

  data() {
    return {
      lang: "cz",
      // imagesCz a imagesEn pro slidery s jazyky; imagesSingle pro jednojazycne
      imagesCz: [],
      imagesEn: [],
      imagesSingle: [],
      saving: false,
      error: null,
      successMessage: null,
    };
  },

  computed: {
    config() {
      return SLIDER_CONFIGS[this.$route.params.key] || null;
    },
    currentType() {
      if (!this.config) return null;
      if (this.config.hasLanguages) {
        return this.lang === "en" ? this.config.typeEn : this.config.typeCz;
      }
      return this.config.type;
    },
    currentImages() {
      if (!this.config) return [];
      if (this.config.hasLanguages) {
        return this.lang === "cz" ? this.imagesCz : this.imagesEn;
      }
      return this.imagesSingle;
    },
  },

  watch: {
    "$route.params.key"() {
      this.init();
    },
  },

  created() {
    this.init();
  },

  methods: {
    init() {
      this.imagesCz = [];
      this.imagesEn = [];
      this.imagesSingle = [];
      this.error = null;
      this.successMessage = null;
      this.lang = "cz";

      if (!this.config) return;

      if (this.config.hasLanguages) {
        this.loadType(this.config.typeCz, "cz");
        this.loadType(this.config.typeEn, "en");
      } else {
        this.loadType(this.config.type, "single");
      }
    },

    loadType(type, target) {
      axios
        .get("/image/list", { params: { type } })
        .then((res) => {
          const sorted = res.data
            .slice()
            .sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { numeric: true })
            )
            .map((img) => ({
              key: ++keyCounter,
              name: img.name,
              src: img.src,
              isNew: false,
            }));
          if (target === "cz") this.imagesCz = sorted;
          else if (target === "en") this.imagesEn = sorted;
          else this.imagesSingle = sorted;
        })
        .catch((err) => (this.error = err));
    },

    switchLang(lang) {
      this.lang = lang;
      this.successMessage = null;
    },

    selectImages(e) {
      Array.from(e.target.files).forEach((file) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (evt) => this.addImageEntry(evt.target.result);
      });
      this.$refs.fileInput.value = "";
    },

    addImageEntry(src) {
      if (!this.config) return;
      const entry = { key: ++keyCounter, src, isNew: true };
      if (this.config.hasLanguages) {
        if (this.lang === "cz") this.imagesCz.push(entry);
        else this.imagesEn.push(entry);
      } else {
        this.imagesSingle.push(entry);
      }
    },

    removeImage(idx) {
      if (!this.config) return;
      if (this.config.hasLanguages) {
        if (this.lang === "cz") this.imagesCz.splice(idx, 1);
        else this.imagesEn.splice(idx, 1);
      } else {
        this.imagesSingle.splice(idx, 1);
      }
    },

    moveUp(idx) {
      const arr = this.currentImages;
      if (idx === 0) return;
      this.$set(arr, idx - 1, arr.splice(idx, 1, arr[idx - 1])[0]);
    },

    moveDown(idx) {
      const arr = this.currentImages;
      if (idx === arr.length - 1) return;
      this.$set(arr, idx + 1, arr.splice(idx, 1, arr[idx + 1])[0]);
    },

    save() {
      const type = this.currentType;
      const images = [...this.currentImages];
      this.saving = true;
      this.error = null;
      this.successMessage = null;

      // 1. Získej aktuální seznam na serveru (pro smazání)
      axios
        .get("/image/list", { params: { type } })
        .then((res) => {
          const existingNames = res.data.map((img) => img.name);

          // 2. Pro každý obrázek získej base64 data
          const dataPromises = images.map((img) => {
            if (img.isNew) return Promise.resolve(img.src);
            return axios
              .get("/image/base64", { params: { type, name: img.name } })
              .then((r) => r.data);
          });

          return Promise.all([
            Promise.resolve(existingNames),
            Promise.all(dataPromises),
          ]);
        })
        .then(([existingNames, imageDatas]) => {
          // 3. Nejdřív nahraj novou podobu všech pozic. Server soubory
          // přepisuje atomicky, takže veřejný slider během ukládání nezmizí.
          const desiredNames = imageDatas.map((data, i) => {
            const name = String(i + 1).padStart(4, "0");
            return { name, data };
          });

          return Promise.all(
            desiredNames.map(({ name, data }) =>
              axios.post("/image", { type, name, data })
            )
          ).then(() => ({ existingNames, desiredNames }));
        })
        .then(({ existingNames, desiredNames }) => {
          // 4. Až po úspěšném uploadu odstraň pozice, které už v seznamu nejsou.
          const desiredNameSet = new Set(
            desiredNames.map(({ name }) => name)
          );
          return Promise.all(
            existingNames
              .filter((name) => !desiredNameSet.has(name))
              .map((name) => axios.post("/image", { type, name, data: "" }))
          );
        })
        .then(() => {
          this.successMessage = "Uloženo.";
          const target = this.config.hasLanguages ? this.lang : "single";
          this.loadType(type, target);
        })
        .catch((err) => {
          this.error = err;
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style scoped>
#adminSlider {
  min-height: 100vh;
  padding-bottom: 4em;
  overflow: hidden;
}

.imageRow {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
}

.orderControls {
  min-width: 52px;
}

.orderNum {
  font-weight: bold;
  font-size: 20px;
  color: #333;
}

.imagePreviewWrap {
  max-width: 700px;
  overflow: hidden;
}

.sliderPreview {
  width: 100%;
  max-height: 280px;
  object-fit: contain;
  border: 1px solid #dee2e6;
  background: #fff;
  display: block;
}

.newBadge {
  position: absolute;
  top: 0;
  left: 0;
  background-color: #28a745;
  color: white;
  padding: 2px 8px;
  font-size: 12px;
  z-index: 1;
  border-bottom-right-radius: 4px;
}
</style>
